import { isLoopbackBaseUrl, isLoopbackHost, type AddinSettings } from "./settings";
import { debugLog } from "./debug";
import { redactSensitiveContent } from "./redaction";
import { startLlmCallSpan, endLlmCallSpan, type TelemetrySpan } from "./telemetry";
import type { ErrorCode } from "./error-codes";

/** A single tool invocation requested by the model. Shared by `Message` (what we
 * send back) and `ChatResponse` (what a provider returns) — the one boundary
 * shape the agent loop trusts to drive tool execution. */
export interface ToolCallPayload {
  id: string;
  type: "function";
  function: { name: string; arguments: string };
}

/** Runtime shape guard for a single tool call, used to filter out malformed
 * entries from an untrusted provider response instead of casting through `any`. */
function isToolCallPayload(value: unknown): value is ToolCallPayload {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.id !== "string" || candidate.type !== "function") {
    return false;
  }
  const fn = candidate.function as Record<string, unknown> | undefined;
  return (
    typeof fn === "object" &&
    fn !== null &&
    typeof fn.name === "string" &&
    typeof fn.arguments === "string"
  );
}

/**
 * Validates and narrows an untrusted `tool_calls` value from a provider
 * response. Returns `null` when the value isn't an array, and silently drops
 * any entry that doesn't match {@link ToolCallPayload} — a malformed entry
 * must not reach the agent loop, which trusts this shape to drive tool
 * execution.
 */
function parseToolCalls(value: unknown): ToolCallPayload[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const valid = value.filter(isToolCallPayload);
  return valid.length > 0 ? valid : null;
}

export interface Message {
  role: "system" | "user" | "assistant" | "tool";
  content: string;
  tool_calls?: ToolCallPayload[];
  tool_call_id?: string;
  name?: string;
}

export interface ToolDefinition {
  type: "function";
  function: {
    name: string;
    description: string;
    parameters: {
      type: "object";
      properties: Record<
        string,
        {
          type: string;
          description?: string;
          enum?: string[];
          items?: { type: string; description?: string };
        }
      >;
      required?: string[];
    };
  };
}

/** Token accounting for a single chat completion, as reported by the provider's
 * `usage` block. Most OpenAI-compatible endpoints include this, but it's not
 * guaranteed — callers must treat it as optional and never assume it's present. */
export interface TokenUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

/** Runtime shape guard for an untrusted `usage` value from a provider response. */
function isTokenUsage(value: unknown): value is TokenUsage {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.prompt_tokens === "number" &&
    typeof candidate.completion_tokens === "number" &&
    typeof candidate.total_tokens === "number"
  );
}

/** Validates and narrows an untrusted `usage` value; `undefined` when absent or malformed. */
function parseUsage(value: unknown): TokenUsage | undefined {
  return isTokenUsage(value) ? value : undefined;
}

export interface ChatResponse {
  content: string | null;
  tool_calls: ToolCallPayload[] | null;
  /** Token counts for this completion, when the provider reports them. */
  usage?: TokenUsage;
}

/**
 * Applies the deterministic pre-send redaction filter (redaction.ts) to every
 * outgoing message's content, when `settings.redactionEnabled` is on
 * (default true). This is the actual pre-send path — the single chokepoint
 * every caller (agent-loop.ts, insights.ts, triage.ts) already funnels
 * through via `sendChat`/`sendChatStream` — so redaction applies uniformly
 * regardless of which feature built the prompt, without each call site
 * having to remember to call it individually. A no-op (returns `messages`
 * unchanged, same array reference) when the toggle is off, matching the
 * ADR-005 gate's "off means byte-for-byte unchanged" contract.
 */
function applyRedaction(messages: Message[], settings: AddinSettings): Message[] {
  if (!settings.redactionEnabled) {
    return messages;
  }
  return messages.map((message) => ({
    ...message,
    content: redactSensitiveContent(message.content),
  }));
}

export class AiError extends Error {
  /** Stable, documented identifier for this failure (error-codes.ts). Shown to
   * the user next to the message and used as the `errorClass` telemetry
   * attribute, so it is a compatibility surface — see that module. */
  readonly errorCode?: ErrorCode;
  /** The underlying error this one wraps, when there is one (A3: never lose
   * the original). Declared here rather than relying on `Error.cause`, which
   * isn't in this project's `es5` lib. */
  readonly cause?: unknown;
  constructor(
    message: string,
    public readonly status?: number,
    errorCode?: ErrorCode,
    cause?: unknown
  ) {
    super(message);
    // Restores the prototype chain lost when TS downlevels classes extending
    // built-in Error to `target: "es5"` (production ts-loader build) —
    // without this, `instanceof AiError` silently returns false in dist/.
    Object.setPrototypeOf(this, AiError.prototype);
    this.name = "AiError";
    this.errorCode = errorCode;
    this.cause = cause;
  }
}

/** Hostname the task pane itself is served from; `""` outside a browser.
 * Read through a helper so {@link isLoopbackFromHostedOrigin} stays testable. */
function pageHostname(): string {
  return typeof location !== "undefined" && typeof location.hostname === "string"
    ? location.hostname
    : "";
}

/**
 * True when the configured endpoint is on the user's own machine but the
 * add-in is served from a public origin. Browsers block that combination twice
 * over: the local server has to opt into CORS for the hosted origin, and
 * Chromium additionally gates public→private requests behind a private-network
 * preflight that local model servers don't answer. Both surface identically as
 * a bare `TypeError: Failed to fetch`, so the distinction has to be made here.
 */
export function isLoopbackFromHostedOrigin(baseUrl: string): boolean {
  return isLoopbackBaseUrl(baseUrl) && !isLoopbackHost(pageHostname());
}

/** User-facing explanation for a request that never reached the provider. */
export function providerUnreachableMessage(baseUrl: string): string {
  if (isLoopbackFromHostedOrigin(baseUrl)) {
    return (
      `Can't reach the local model server at ${baseUrl} from a hosted add-in — ` +
      `the browser blocks requests from a public site to your machine. ` +
      `Point Base URL at an HTTPS-reachable endpoint, or run the add-in from localhost.`
    );
  }
  return (
    `Can't reach the AI provider at ${baseUrl}. Check the base URL and your network ` +
    `connection, and that the endpoint allows cross-origin requests from this add-in.`
  );
}

/**
 * `fetch` for provider calls. A network-level failure (CORS rejection, blocked
 * private-network request, DNS, refused connection) rejects with a bare
 * `TypeError: Failed to fetch` that tells the user nothing — this maps it to an
 * `AiError` carrying an actionable message and the `NETWORK_UNREACHABLE` code,
 * keeping the original as `cause`. HTTP error *responses* are not touched here;
 * each caller maps those from the status.
 */
async function fetchProvider(
  baseUrl: string,
  url: string,
  // `Parameters<typeof fetch>[1]` rather than `RequestInit`: the DOM *type* is
  // not one of the browser globals eslint knows about, so naming it directly
  // trips no-undef.
  init?: Parameters<typeof fetch>[1]
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    throw new AiError(providerUnreachableMessage(baseUrl), undefined, "NETWORK_UNREACHABLE", e);
  }
}

/** Error class label for a telemetry span — `AiError`'s `errorCode` when
 * present (e.g. `"NO_API_KEY"`), otherwise the JS error name. Never the
 * error message, which could echo request/response content. */
function errorClassOf(e: unknown): string {
  if (e instanceof AiError) {
    return e.errorCode ?? "AiError";
  }
  return e instanceof Error ? e.name : "UnknownError";
}

export async function sendChat(
  messages: Message[],
  tools: ToolDefinition[],
  settings: AddinSettings,
  parentSpan: TelemetrySpan = null
): Promise<ChatResponse> {
  const span = startLlmCallSpan(parentSpan, { model: settings.model, streaming: false });
  try {
    // Loopback endpoints (e.g. a local Ollama) accept requests without an API key.
    if (!settings.apiKey && !isLoopbackBaseUrl(settings.baseUrl)) {
      throw new AiError("API key is not configured", undefined, "NO_API_KEY");
    }

    const body: Record<string, unknown> = {
      model: settings.model,
      messages: applyRedaction(messages, settings),
    };
    if (tools.length > 0) {
      body.tools = tools;
      body.tool_choice = "auto";
    }

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (settings.apiKey) {
      headers.Authorization = `Bearer ${settings.apiKey}`;
    }

    const response = await fetchProvider(settings.baseUrl, `${settings.baseUrl}/chat/completions`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new AiError(
        `AI provider error: ${response.statusText}`,
        response.status,
        "PROVIDER_HTTP_ERROR"
      );
    }

    const data = (await response.json()) as {
      choices?: Array<{ message?: { content: string | null; tool_calls?: unknown } }>;
      usage?: unknown;
    };
    const message = data.choices?.[0]?.message;
    if (!message) {
      throw new AiError(
        "AI provider returned an empty response",
        undefined,
        "PROVIDER_EMPTY_RESPONSE"
      );
    }

    const usage = parseUsage(data.usage);
    endLlmCallSpan(span, {
      success: true,
      inputTokens: usage?.prompt_tokens,
      outputTokens: usage?.completion_tokens,
    });
    return {
      content: message.content ?? null,
      tool_calls: parseToolCalls(message.tool_calls),
      usage,
    };
  } catch (e) {
    endLlmCallSpan(span, { success: false, errorClass: errorClassOf(e) });
    throw e;
  }
}

interface StreamToolCallDelta {
  index?: number;
  id?: string;
  function?: { name?: string; arguments?: string };
}

interface StreamChunk {
  choices?: Array<{
    delta?: {
      content?: string | null;
      tool_calls?: StreamToolCallDelta[];
    };
  }>;
  /** OpenAI-compatible SSE streams that honor `stream_options.include_usage`
   * carry this only on the final chunk (often alongside an empty `choices`). */
  usage?: unknown;
}

/**
 * Sends a chat completion request with `stream: true` and reads the response as
 * Server-Sent Events, invoking `onDelta` with each incremental content chunk as it
 * arrives. Only the **content** stream is surfaced incrementally: if the provider
 * emits `tool_calls` deltas (meaning the model wants to call a tool rather than
 * answer directly), those are accumulated silently and returned exactly like
 * `sendChat` would — the caller sees no partial output for tool-calling turns.
 *
 * Falls back to a single non-streamed read if the runtime's `fetch` does not
 * expose a readable `response.body` (older environments) — behaviorally
 * equivalent to `sendChat`, just without incremental `onDelta` calls.
 */
export async function sendChatStream(
  messages: Message[],
  tools: ToolDefinition[],
  settings: AddinSettings,
  onDelta: (chunk: string) => void,
  parentSpan: TelemetrySpan = null
): Promise<ChatResponse> {
  const span = startLlmCallSpan(parentSpan, { model: settings.model, streaming: true });
  try {
    return await sendChatStreamInner(messages, tools, settings, onDelta, span);
  } catch (e) {
    endLlmCallSpan(span, { success: false, errorClass: errorClassOf(e) });
    throw e;
  }
}

async function sendChatStreamInner(
  messages: Message[],
  tools: ToolDefinition[],
  settings: AddinSettings,
  onDelta: (chunk: string) => void,
  span: TelemetrySpan
): Promise<ChatResponse> {
  if (!settings.apiKey && !isLoopbackBaseUrl(settings.baseUrl)) {
    throw new AiError("API key is not configured", undefined, "NO_API_KEY");
  }

  const body: Record<string, unknown> = {
    model: settings.model,
    messages: applyRedaction(messages, settings),
    stream: true,
    // Asks OpenAI-compatible providers to emit a final SSE chunk carrying the
    // `usage` block (prompt/completion/total tokens) — without this, most
    // providers omit usage entirely from a streamed response. Providers that
    // don't recognize the option are expected to ignore it.
    stream_options: { include_usage: true },
  };
  if (tools.length > 0) {
    body.tools = tools;
    body.tool_choice = "auto";
  }

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (settings.apiKey) {
    headers.Authorization = `Bearer ${settings.apiKey}`;
  }

  const response = await fetchProvider(settings.baseUrl, `${settings.baseUrl}/chat/completions`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new AiError(
      `AI provider error: ${response.statusText}`,
      response.status,
      "PROVIDER_HTTP_ERROR"
    );
  }

  if (!response.body) {
    throw new AiError(
      "AI provider did not return a streamable response body",
      undefined,
      "PROVIDER_NOT_STREAMABLE"
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let content = "";
  let hasToolCalls = false;
  let usage: TokenUsage | undefined;
  const toolCallsByIndex = new Map<
    number,
    { id: string; type: "function"; function: { name: string; arguments: string } }
  >();

  const processLine = (line: string): void => {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data:")) {
      return;
    }
    const payload = trimmed.slice("data:".length).trim();
    if (payload === "" || payload === "[DONE]") {
      return;
    }

    let chunk: StreamChunk;
    try {
      chunk = JSON.parse(payload) as StreamChunk;
    } catch {
      // Malformed/partial SSE line — ignore rather than crash the stream.
      return;
    }

    // The usage-carrying chunk is typically the final one and often has an
    // empty `choices` array — check it before the delta early-return below,
    // or it would never be seen.
    if (chunk.usage !== undefined) {
      const parsed = parseUsage(chunk.usage);
      if (parsed) {
        usage = parsed;
      }
    }

    const delta = chunk.choices?.[0]?.delta;
    if (!delta) {
      return;
    }

    if (delta.tool_calls && delta.tool_calls.length > 0) {
      hasToolCalls = true;
      for (const toolCallDelta of delta.tool_calls) {
        const index = toolCallDelta.index ?? 0;
        const existing = toolCallsByIndex.get(index) ?? {
          id: "",
          type: "function" as const,
          function: { name: "", arguments: "" },
        };
        if (toolCallDelta.id) {
          existing.id = toolCallDelta.id;
        }
        if (toolCallDelta.function?.name) {
          existing.function.name += toolCallDelta.function.name;
        }
        if (toolCallDelta.function?.arguments) {
          existing.function.arguments += toolCallDelta.function.arguments;
        }
        toolCallsByIndex.set(index, existing);
      }
    }

    if (typeof delta.content === "string" && delta.content.length > 0) {
      content += delta.content;
      if (!hasToolCalls) {
        onDelta(delta.content);
      }
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      processLine(line);
    }
  }
  if (buffer.length > 0) {
    processLine(buffer);
  }

  endLlmCallSpan(span, {
    success: true,
    inputTokens: usage?.prompt_tokens,
    outputTokens: usage?.completion_tokens,
  });

  if (hasToolCalls) {
    return {
      content: content.length > 0 ? content : null,
      tool_calls: Array.from(toolCallsByIndex.values()),
      usage,
    };
  }

  return { content: content.length > 0 ? content : null, tool_calls: null, usage };
}

/**
 * Lists the model IDs available from the configured OpenAI-compatible endpoint
 * via `GET {baseUrl}/models`. Returns IDs sorted alphabetically. Throws
 * `AiError` if the endpoint is unreachable or does not support `/models`
 * (e.g. some minimal gateways) — callers should treat this as non-blocking
 * and fall back to free-text model entry.
 */
export async function listModels(settings: AddinSettings): Promise<string[]> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (settings.apiKey) {
    headers.Authorization = `Bearer ${settings.apiKey}`;
  }

  const response = await fetchProvider(settings.baseUrl, `${settings.baseUrl}/models`, { headers });

  if (!response.ok) {
    throw new AiError(
      `AI provider error: ${response.statusText}`,
      response.status,
      "PROVIDER_HTTP_ERROR"
    );
  }

  const data = (await response.json()) as { data?: Array<{ id?: unknown }> };
  const ids = (data.data ?? [])
    .map((m) => m.id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  // Instrumentation: an OK response that yields zero models is a real, silent
  // failure mode (endpoint doesn't implement /models, or a non-standard schema).
  // Log the count so it's diagnosable rather than looking like the button did
  // nothing (Bug 5). Enable with localStorage.aiAssistantDebug = "1".
  debugLog("models:loaded", { count: ids.length });
  return ids.sort((a, b) => a.localeCompare(b));
}
